// components/ConfirmDialog.jsx
import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as Icons from "lucide-react";

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onCancel,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "danger", // 'danger' | 'primary'
}) => {
  const dialogId = useId();
  const cancelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const ANIM_MS = 160;

  const [shouldRender, setShouldRender] = useState(Boolean(isOpen));
  const [isActive, setIsActive] = useState(Boolean(isOpen));

  useEffect(() => {
    if (isOpen) {
      clearTimeout(closeTimerRef.current);
      setShouldRender(true);
      requestAnimationFrame(() => {
        setIsActive(true);
        // Focus the safe (cancel) action by default.
        setTimeout(() => cancelRef.current?.focus(), 0);
      });
      return;
    }

    // play exit animation then unmount
    setIsActive(false);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setShouldRender(false), ANIM_MS);

    return () => clearTimeout(closeTimerRef.current);
  }, [isOpen]);


  useEffect(() => {
    if (!shouldRender) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onCancel?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shouldRender, onCancel]);

  if (!shouldRender) return null;

  const isDanger = tone === "danger";
  const confirmClass = isDanger
    ? "bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500/30"
    : "bg-slate-900 hover:bg-slate-800 focus-visible:ring-slate-900/30";

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[200] flex items-center justify-center p-4 " +
        "bg-slate-900/50 backdrop-blur-[2px] transition-opacity " +
        (isActive ? "opacity-100" : "opacity-0") +
        (isActive ? "" : " pointer-events-none")
      }
      style={{ transitionDuration: `${ANIM_MS}ms` }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${dialogId}-title`}
      aria-describedby={`${dialogId}-desc`}
      onMouseDown={(e) => {
        // click outside closes
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div
        className={
          "w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden " +
          "transform transition-transform " +
          (isActive ? "scale-100 translate-y-0" : "scale-[0.98] translate-y-1")
        }
        style={{ transitionDuration: `${ANIM_MS}ms` }}
      >
        <div className="px-6 py-5 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={
                  "h-10 w-10 rounded-xl border flex items-center justify-center " +
                  (isDanger ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50")
                }
              >
                {isDanger ? (
                  <Icons.LogOut size={18} className="text-rose-600" aria-hidden="true" />
                ) : (
                  <Icons.HelpCircle size={18} className="text-slate-700" aria-hidden="true" />
                )}
              </div>
              <div className="min-w-0">
                <h2 id={`${dialogId}-title`} className="text-base font-semibold text-slate-900 truncate">
                  {title}
                </h2>
                <p id={`${dialogId}-desc`} className="mt-1 text-xs text-slate-600">
                  {message}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/30"
              aria-label="Close"
            >
              <Icons.X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 bg-slate-50/40">
          <div className="flex items-center justify-end gap-3">
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-xs font-medium text-slate-800 hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/30"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={
                "inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-xs font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 " +
                confirmClass
              }
            >
              <Icons.LogOut size={14} aria-hidden="true" />
              {confirmText} 
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
